export const toISO = (d) => (d ? d.split(".").reverse().join("-") : "");

export const toNum = (s) => (s ? parseFloat(s.replace(/\./g, "").replace(",", ".")) : null);

export const fmtEuro = (n) => {
  const [int, dec] = Math.abs(n).toFixed(2).split(".");
  return `${n < 0 ? "-" : ""}${int.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${dec}`;
};

export const EMPTY_FILTERS = { op: "", val: "", desc: "", deb: "", cred: "" };

const pad = (n) => String(n).padStart(2, "0");
const fullYear = (y) => (y.length === 2 ? `20${y}` : y);
const lastDay = (y, m) => new Date(Number(y), Number(m), 0).getDate();
const validDM = (d, m) => m >= 1 && m <= 12 && d >= 1 && d <= 31;

const parseDateToken = (tok) => {
  let m = tok.match(/^(\d{2})(\d{2})(\d{4})$/);
  if (/^20\d{2}$/.test(tok)) return { from: `${tok}-01-01`, to: `${tok}-12-31` };
  if (m) tok = `${m[1]}.${m[2]}.${m[3]}`;
  m = tok.match(/^(\d{1,2})[./-](\d{4})$/);
  if (m && validDM(1, +m[1])) {
    const mm = pad(m[1]);
    return { from: `${m[2]}-${mm}-01`, to: `${m[2]}-${mm}-${pad(lastDay(m[2], mm))}` };
  }
  m = tok.match(/^(\d{1,2})[./-](\d{1,2})(?:[./-](\d{2}|\d{4}))?$/);
  if (!m || !validDM(+m[1], +m[2])) return null;
  if (!m[3]) return { md: `${pad(m[2])}-${pad(m[1])}` };
  const iso = `${fullYear(m[3])}-${pad(m[2])}-${pad(m[1])}`;
  return { from: iso, to: iso };
};

export const parseDateFilter = (s) => {
  const t = s.trim();
  if (!t) return null;
  const compact = t.match(/^(\d{1,2}[./]\d{1,2}[./]\d{2,4}|\d{1,2}[./]\d{4})-(\d{1,2}[./]\d{1,2}[./]\d{2,4}|\d{1,2}[./]\d{4})$/);
  const parts = compact ? [compact[1], compact[2]] : t.split(/\s*(?:\s-\s|–|—|\bal\b|\ba\b|\s+)\s*/i).filter(Boolean);
  if (parts.length === 1) return parseDateToken(parts[0]) || "invalid";
  if (parts.length !== 2) return "invalid";
  const [a, b] = parts.map(parseDateToken);
  if (!a?.from || !b?.to) return "invalid";
  return a.from <= b.to ? { from: a.from, to: b.to } : { from: b.from, to: a.to };
};

const dateOk = (iso, input) => {
  const f = parseDateFilter(input);
  if (f === null || f === "invalid") return true;
  if (f.md) return iso.slice(5) === f.md;
  return iso >= f.from && iso <= f.to;
};

export const parseAmountInput = (s) => {
  const t = s.replace(/[€\s]/g, "");
  if (!t) return null;
  const norm = t.includes(",") ? t.replace(/\./g, "").replace(",", ".") : /^\d{1,3}(\.\d{3})+$/.test(t) ? t.replace(/\./g, "") : t;
  const n = /^-?\d+(\.\d+)?$/.test(norm) ? parseFloat(norm) : NaN;
  return isNaN(n) ? NaN : n;
};

const amountOk = (v, input) => {
  const target = parseAmountInput(input);
  if (target === null) return true;
  return v !== null && Math.abs(v - target) < 0.005;
};

export const prepareRows = (txs) =>
  txs.map((t, idx) => ({
    ...t, idx,
    opISO: toISO(t.data_operazione), valISO: toISO(t.data_valuta),
    deb: toNum(t.addebiti), cred: toNum(t.accrediti), descLow: t.descrizione.toLowerCase(),
  }));

export const filterRows = (rows, f) => {
  const words = f.desc.toLowerCase().split(/\s+/).filter(Boolean);
  return rows.filter((r) =>
    dateOk(r.opISO, f.op) &&
    dateOk(r.valISO, f.val) &&
    words.every((w) => r.descLow.includes(w)) &&
    amountOk(r.deb, f.deb) &&
    amountOk(r.cred, f.cred));
};

const SORT_KEYS = { op: "opISO", val: "valISO", desc: "descLow", deb: "deb", cred: "cred" };

export const sortRows = (rows, sort) => {
  if (!sort) return rows;
  const key = SORT_KEYS[sort.col];
  const dir = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = a[key], y = b[key];
    if (x === y) return a.idx - b.idx;
    if (x === null) return 1;
    if (y === null) return -1;
    return (x > y ? 1 : -1) * dir;
  });
};

export const activeFilterCount = (f) =>
  Object.keys(EMPTY_FILTERS).filter((k) => f[k] !== EMPTY_FILTERS[k] && !((k === "op" || k === "val") && parseDateFilter(f[k]) === "invalid")).length;

const dupKey = (r) => [r.opISO, r.valISO, r.descLow.replace(/\s+/g, " ").trim(), r.deb, r.cred].join("|");

export const findDuplicates = (rows) => {
  const groups = new Map();
  rows.forEach((r) => {
    const k = dupKey(r);
    groups.set(k, [...(groups.get(k) || []), r.idx]);
  });
  const info = new Map();
  let extra = 0;
  let nGroups = 0;
  groups.forEach((ids, k) => {
    if (ids.length < 2) return;
    nGroups += 1;
    extra += ids.length - 1;
    ids.forEach((idx, pos) => info.set(idx, { key: k, pos, size: ids.length }));
  });
  return { info, extra, nGroups };
};
